import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, DollarSign, Receipt, AlertCircle, CalendarDays, RotateCcw, CheckCircle2, XCircle, MinusCircle, ClipboardCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";

// BizLedger Pro brand colors for charts
const CHART_COLORS = ["#e85d24", "#00E096", "#3B82F6", "#FFB946", "#8B5CF6", "#EC4899", "#14B8A6", "#FF3B30"];

function KpiCard({ title, value, icon: Icon, trend, color }) {
  return (
    <Card className="border-border/50 hover:border-primary/30 transition-colors card-glow" style={{ background: 'hsl(var(--card))' }}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold font-mono mt-1" style={{ color }}>{value}</p>
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
        {trend !== undefined && (
          <p className={`text-xs mt-2 flex items-center gap-1 ${trend >= 0 ? 'text-profit' : 'text-loss'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}% from last month
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg p-3 border border-border text-sm glass">
      <p className="text-muted-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-mono" style={{ color: p.color }}>
          {p.name}: ${Number(p.value).toLocaleString()}
        </p>
      ))}
    </div>
  );
};

const fmtCloseMonth = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: 'short', year: 'numeric' });
};

function CloseCheck({ ok, okLabel, failLabel }) {
  if (ok === null) return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground/60">
      <MinusCircle className="w-3.5 h-3.5" /> {okLabel}
    </span>
  );
  return ok ? (
    <span className="flex items-center gap-1 text-xs text-profit">
      <CheckCircle2 className="w-3.5 h-3.5" /> {okLabel}
    </span>
  ) : (
    <span className="flex items-center gap-1 text-xs text-warning">
      <XCircle className="w-3.5 h-3.5" /> {failLabel}
    </span>
  );
}

function MonthlyCloseCard({ companyId }) {
  const [months, setMonths] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/monthly-close`)
      .then(res => setMonths(res.data.months))
      .catch(() => setMonths([]));
  }, [companyId]);

  if (!months) return null;
  const active = months.filter(m => m.has_activity);
  if (active.length === 0) return null;

  return (
    <Card className="border-border/50 glass" data-testid="monthly-close-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading text-white flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-primary" /> Monthly Close Checklist
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {active.map(m => (
          <div key={m.month} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 border-b border-border/30 last:border-0"
            data-testid={`close-month-${m.month}`}>
            <span className="text-sm text-white font-mono w-20">{fmtCloseMonth(m.month)}</span>
            <CloseCheck
              ok={m.statements_ok}
              okLabel="Statements"
              failLabel={`Statement missing: ${m.missing_accounts.join(", ")}`}
            />
            <CloseCheck
              ok={m.categorized_ok}
              okLabel="Categorized"
              failLabel={`${m.uncategorized_count} uncategorized`}
            />
            <CloseCheck
              ok={m.review_ok}
              okLabel="Reviewed"
              failLabel={`${m.needs_review_count} need review`}
            />
            <span className="ml-auto">
              {m.closed ? (
                <Badge variant="outline" className="text-xs border-profit/40 text-profit" data-testid={`close-status-${m.month}`}>
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Closed
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs border-warning/40 text-warning" data-testid={`close-status-${m.month}`}>
                  Open
                </Badge>
              )}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function getPresetRange(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split("T")[0];
  switch (preset) {
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: fmt(start), end: fmt(today) };
    }
    case "last_month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case "this_quarter": {
      const qMonth = Math.floor(today.getMonth() / 3) * 3;
      const start = new Date(today.getFullYear(), qMonth, 1);
      return { start: fmt(start), end: fmt(today) };
    }
    case "this_year": {
      const start = new Date(today.getFullYear(), 0, 1);
      return { start: fmt(start), end: fmt(today) };
    }
    case "last_year": {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { start: fmt(start), end: fmt(end) };
    }
    case "last_90": {
      const start = new Date(today);
      start.setDate(start.getDate() - 90);
      return { start: fmt(start), end: fmt(today) };
    }
    default:
      return { start: "", end: "" };
  }
}

export default function DashboardPage({ companyId, company }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activePreset, setActivePreset] = useState("all_time");

  const fetchDashboard = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.get(`/companies/${companyId}/dashboard`, { params })
      .then(res => setData(res.data))
      .catch(err => console.error("Dashboard error:", err))
      .finally(() => setLoading(false));
  }, [companyId, startDate, endDate]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handlePreset = (preset) => {
    setActivePreset(preset);
    if (preset === "all_time") {
      setStartDate("");
      setEndDate("");
    } else {
      const { start, end } = getPresetRange(preset);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setActivePreset("all_time");
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-64 bg-secondary" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 bg-secondary" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-80 bg-secondary" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { summary, monthly_data, cash_flow, category_breakdown, recent_transactions } = data;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">{company?.name || "Dashboard"}</h1>
          <p className="text-muted-foreground mt-1">Financial overview</p>
        </div>
        {summary.uncategorized > 0 && (
          <Badge variant="outline" className="border-warning/50 text-warning" data-testid="uncategorized-badge">
            <AlertCircle className="w-3.5 h-3.5 mr-1" /> {summary.uncategorized} uncategorized
          </Badge>
        )}
      </div>

      {/* Date Range Filter */}
      <Card className="border-border/50 glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <CalendarDays className="w-4 h-4 text-primary" />
            <div className="flex gap-1.5 flex-wrap">
              {[
                { key: "all_time", label: "All Time" },
                { key: "this_month", label: "This Month" },
                { key: "last_month", label: "Last Month" },
                { key: "this_quarter", label: "This Quarter" },
                { key: "last_90", label: "Last 90 Days" },
                { key: "this_year", label: "This Year" },
                { key: "last_year", label: "Last Year" },
              ].map(p => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={activePreset === p.key ? "default" : "ghost"}
                  className={`h-8 text-xs ${activePreset === p.key ? 'btn-primary-gradient' : 'text-muted-foreground hover:text-white'}`}
                  onClick={() => handlePreset(p.key)}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setActivePreset("custom"); }}
                className="h-8 w-[145px] bg-secondary border-border text-white text-xs"
                data-testid="dashboard-start-date"
              />
              <span className="text-muted-foreground text-xs">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setActivePreset("custom"); }}
                className="h-8 w-[145px] bg-secondary border-border text-white text-xs"
                data-testid="dashboard-end-date"
              />
              {(startDate || endDate) && (
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-white"
                  onClick={handleClearDates} data-testid="clear-date-filter">
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <KpiCard title="Total Income" value={`$${summary.total_income.toLocaleString()}`} icon={TrendingUp} color="#00E096" />
        <KpiCard title="Total Expenses" value={`$${summary.total_expenses.toLocaleString()}`} icon={TrendingDown} color="#FF3B30" />
        <KpiCard title="Net Profit" value={`$${summary.net_profit.toLocaleString()}`} icon={DollarSign}
          color={summary.net_profit >= 0 ? "#00E096" : "#FF3B30"} />
        <KpiCard title="Transactions" value={summary.total_transactions.toLocaleString()} icon={Receipt} color="#e85d24" />
      </div>

      {/* Monthly Close Checklist */}
      <MonthlyCloseCard companyId={companyId} />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income vs Expenses Bar Chart */}
        <Card className="border-border/50 glass">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading text-white">Income vs Expenses</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {monthly_data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={250}>
                <BarChart data={monthly_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 15% 22%)" />
                  <XAxis dataKey="month" tick={{ fill: '#A0AEC0', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#A0AEC0', fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="income" fill="#00E096" radius={[4, 4, 0, 0]} name="Income" />
                  <Bar dataKey="expenses" fill="#FF3B30" radius={[4, 4, 0, 0]} name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                No data yet. Add transactions to see charts.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cash Flow Line Chart */}
        <Card className="border-border/50 glass">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading text-white">Cash Flow</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {cash_flow.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={250}>
                <LineChart data={cash_flow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 15% 22%)" />
                  <XAxis dataKey="month" tick={{ fill: '#A0AEC0', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#A0AEC0', fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="balance" stroke="#e85d24" strokeWidth={2} dot={{ fill: '#e85d24', r: 4 }} name="Balance" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                No data yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row - Category Breakdown + Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Expense Category Donut */}
        <Card className="lg:col-span-2 border-border/50 glass">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading text-white">Expense Categories</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {category_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={250}>
                <PieChart>
                  <Pie data={category_breakdown} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                    paddingAngle={2} dataKey="value" nameKey="name">
                    {category_breakdown.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => `$${Number(val).toLocaleString()}`}
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                    itemStyle={{ color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                No expenses categorized yet.
              </div>
            )}
            {category_breakdown.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {category_breakdown.slice(0, 5).map((c, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    {c.name}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="lg:col-span-3 border-border/50 glass">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading text-white">Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {recent_transactions.length > 0 ? (
              <div className="space-y-2">
                {recent_transactions.slice(0, 8).map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 border-b border-border/30 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{t.description}</p>
                      <p className="text-xs text-muted-foreground">{t.date} &middot; {t.category_name}</p>
                    </div>
                    <span className={`font-mono text-sm font-medium ml-4 ${t.type === 'income' ? 'text-profit' : 'text-loss'}`}>
                      {t.type === 'income' ? '+' : '-'}${t.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">
                No transactions yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
